(function () {
  'use strict';

  // Module-scoped: survives across init() calls for proper cleanup on Theme Editor reload
  var removeDocumentEscapeListener = null;

  // Cleanup side-effects of a previously-active drawer before removing it from DOM
  // (Theme Editor section reload can happen while drawer is open)
  function cleanupExistingDrawer(existingDrawer) {
    if (!existingDrawer.classList.contains('active')) return;
    existingDrawer.classList.remove('active');
    existingDrawer.setAttribute('aria-hidden', 'true');
    existingDrawer.setAttribute('aria-modal', 'false');
    if (document.body) {
      document.body.classList.remove('overflow-hidden');
    }
    document.querySelectorAll('[data-locale-trigger]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
  }

  // Full teardown of body-level drawer + module state — for when header reloads without a drawer
  // (e.g. both country + language selectors disabled in Theme Editor)
  function cleanupBodyDrawer() {
    if (removeDocumentEscapeListener) {
      removeDocumentEscapeListener();
      removeDocumentEscapeListener = null;
    }
    var existingDrawer = document.querySelector('body > [data-locale-drawer]');
    if (existingDrawer) {
      cleanupExistingDrawer(existingDrawer);
      existingDrawer.remove();
    }
    delete window.openLocaleDrawer;
  }

  function init() {
    var drawer = document.querySelector('[data-locale-drawer]');
    if (!drawer || drawer.dataset.initialized) return;

    // 1. Cleanup old document-level Escape listener FIRST (closure may reference stale drawer)
    if (removeDocumentEscapeListener) {
      removeDocumentEscapeListener();
      removeDocumentEscapeListener = null;
    }

    // 2. Move drawer to body to escape header stacking context (z-index: 3-4)
    if (document.body) {
      var existingDrawer = document.querySelector('body > [data-locale-drawer]');
      if (existingDrawer && existingDrawer !== drawer) {
        cleanupExistingDrawer(existingDrawer);
        existingDrawer.remove();
      }
      document.body.appendChild(drawer);
    }

    drawer.dataset.initialized = 'true';

    var overlay = drawer.querySelector('[data-locale-overlay]');
    var closeBtn = drawer.querySelector('[data-locale-close]');
    var backBtn = drawer.querySelector('[data-locale-back]');
    var titleEl = drawer.querySelector('[data-locale-title]');
    var views = drawer.querySelectorAll('[data-locale-view]');
    var goButtons = drawer.querySelectorAll('[data-locale-go]');
    var form = drawer.querySelector('#LocaleDrawerForm');
    var saveBtn = drawer.querySelector('[data-locale-save]');
    var hiddenCountryInput = drawer.querySelector('[data-locale-hidden-country]');
    var hiddenLangInput = drawer.querySelector('[data-locale-hidden-lang]');
    var triggers = document.querySelectorAll('[data-locale-trigger]');

    var menuCountryRow = drawer.querySelector('[data-locale-go="country"]');
    var menuLanguageRow = drawer.querySelector('[data-locale-go="language"]');

    var titles = {
      menu: drawer.dataset.titleMenu,
      country: drawer.dataset.titleCountry,
      language: drawer.dataset.titleLanguage,
    };

    var triggerElement = null;
    var isSubmittingLocale = false;

    // Current values from Liquid-rendered hidden inputs (never mutated after init)
    var currentCountryIso = hiddenCountryInput ? hiddenCountryInput.value : null;
    var currentLocaleCode = hiddenLangInput ? hiddenLangInput.value : null;

    // Pending values — track radio selections until user commits via save
    var pendingCountryIso = currentCountryIso;
    var pendingLocaleCode = currentLocaleCode;

    // Safe radio lookup — avoids selector string building with user-supplied values
    function findRadioByValue(name, value) {
      return Array.from(drawer.querySelectorAll('input[name="' + name + '"]')).find(function (radio) {
        return radio.value === value;
      });
    }

    function hasPendingLocaleChange() {
      return pendingCountryIso !== currentCountryIso || pendingLocaleCode !== currentLocaleCode;
    }

    // Single source of truth for save button disabled state (outside submit lock)
    function updateSaveButtonState() {
      if (saveBtn) saveBtn.disabled = !hasPendingLocaleChange();
    }

    // Freeze/thaw radio groups during in-flight submit — prevents stale capture
    function setCountryInputsDisabled(disabled) {
      drawer.querySelectorAll('input[name="country_code_drawer"]').forEach(function (input) {
        input.disabled = disabled;
      });
    }

    function setLanguageInputsDisabled(disabled) {
      drawer.querySelectorAll('input[name="locale_code_drawer"]').forEach(function (input) {
        input.disabled = disabled;
      });
    }

    // Menu row live-update — must sync img.alt with src for accessible name
    function updateMenuCountryRow(radio) {
      if (!menuCountryRow) return;
      var img = menuCountryRow.querySelector('[data-country-flag]');
      if (img) {
        img.src = radio.dataset.flagUrl;
        img.alt = radio.dataset.countryName;
      }
      var nameEl = menuCountryRow.querySelector('[data-country-name]');
      if (nameEl) nameEl.textContent = radio.dataset.countryName;
      var currencyEl = menuCountryRow.querySelector('[data-country-currency]');
      if (currencyEl) currencyEl.textContent = (radio.dataset.currencySymbol || '').trim();
    }

    function updateMenuLanguageRow(radio) {
      if (!menuLanguageRow) return;
      var endonymEl = menuLanguageRow.querySelector('[data-language-endonym]');
      if (endonymEl) endonymEl.textContent = radio.dataset.endonym;
    }

    function showView(name) {
      views.forEach(function (v) {
        var isActive = v.getAttribute('data-locale-view') === name;
        v.hidden = !isActive;
        v.querySelectorAll('button, input, select, textarea').forEach(function (el) {
          el.disabled = !isActive;
        });
      });

      if (isSubmittingLocale) {
        // Lock: override blanket enable, disable all submit-related controls
        if (saveBtn) saveBtn.disabled = true;
        setCountryInputsDisabled(true);
        setLanguageInputsDisabled(true);
      } else {
        // Not locked: save button state driven by dirty guard, not view activity
        updateSaveButtonState();
      }

      titleEl.textContent = titles[name] || '';
      backBtn.hidden = name === 'menu';
      backBtn.disabled = name === 'menu';

      // Re-trap focus when switching views in open drawer
      if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(drawer, name === 'menu' ? closeBtn : backBtn);
      }
    }

    function open(trigger) {
      triggerElement = trigger || document.activeElement;

      // Close menu drawer if open (mobile conflict resolution)
      var openMenuDetails = document.querySelector('header-drawer details[open]');
      if (openMenuDetails) {
        openMenuDetails.querySelector('summary').click();
      }

      // Close search modal if open
      var openSearchDetails = document.querySelector('details-modal details[open]');
      if (openSearchDetails) {
        var modalCustomElement = openSearchDetails.closest('details-modal');
        if (modalCustomElement && typeof modalCustomElement.close === 'function') {
          modalCustomElement.close(false);
        } else {
          openSearchDetails.removeAttribute('open');
          document.body.classList.remove('overflow-hidden');
        }
      }

      // Close cart drawer if open
      var openCartDrawer = document.querySelector('cart-drawer.active');
      if (openCartDrawer && typeof openCartDrawer.close === 'function') {
        openCartDrawer.close();
      }

      // aria-expanded: only on the trigger that opened the drawer
      if (triggerElement && triggerElement.hasAttribute && triggerElement.hasAttribute('data-locale-trigger')) {
        triggerElement.setAttribute('aria-expanded', 'true');
      }

      showView('menu');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.setAttribute('aria-modal', 'true');
      drawer.classList.add('active');
      document.body.classList.add('overflow-hidden');

      if (typeof trapFocus === 'function') {
        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (!reducedMotion || reducedMotion.matches) {
          trapFocus(drawer, closeBtn);
        } else {
          var panel = drawer.querySelector('.locale-drawer__panel');
          panel.addEventListener(
            'transitionend',
            function () {
              // Guard against rapid open→close race: transitionend may fire on the closing transition
              if (drawer.classList.contains('active')) {
                trapFocus(drawer, closeBtn);
              }
            },
            { once: true }
          );
        }
      }
    }

    function close() {
      drawer.classList.remove('active');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('aria-modal', 'false');
      document.body.classList.remove('overflow-hidden');

      // Reset aria-expanded on all triggers
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });

      // Compute focus target once: if trigger is visible, focus it; otherwise focus hamburger summary
      var focusTarget =
        triggerElement && triggerElement.offsetParent !== null
          ? triggerElement
          : document.querySelector('header-drawer summary');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(focusTarget);
      }

      // Pending state reset — skip if submit in flight (hidden inputs were just synced)
      if (isSubmittingLocale) return;

      pendingCountryIso = currentCountryIso;
      pendingLocaleCode = currentLocaleCode;

      var currentCountryRadio = findRadioByValue('country_code_drawer', currentCountryIso);
      var currentLangRadio = findRadioByValue('locale_code_drawer', currentLocaleCode);
      if (currentCountryRadio) {
        currentCountryRadio.checked = true;
        updateMenuCountryRow(currentCountryRadio);
      }
      if (currentLangRadio) {
        currentLangRadio.checked = true;
        updateMenuLanguageRow(currentLangRadio);
      }
      if (saveBtn) saveBtn.disabled = true;
    }

    // Event listeners
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', close);

    backBtn.addEventListener('click', function () {
      showView('menu');
    });

    goButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        showView(btn.getAttribute('data-locale-go'));
      });
    });

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        open(trigger);
      });
    });

    // Shared selection appliers — used by both click (commit + return to menu, ESN UX)
    // and change (keyboard arrow nav, update pending only, stay in list).
    function applyCountrySelection(radio) {
      pendingCountryIso = radio.value;
      updateMenuCountryRow(radio);
      updateSaveButtonState();
    }

    function applyLanguageSelection(radio) {
      pendingLocaleCode = radio.value;
      updateMenuLanguageRow(radio);
      updateSaveButtonState();
    }

    // Country radios — click commits (incl. same-value clicks per ESN UX); change handles keyboard arrow nav
    drawer.querySelectorAll('input[name="country_code_drawer"]').forEach(function (radio) {
      radio.addEventListener('click', function () {
        applyCountrySelection(radio);
        showView('menu');
      });
      radio.addEventListener('change', function () {
        applyCountrySelection(radio);
      });
    });

    // Language radios — same pattern
    drawer.querySelectorAll('input[name="locale_code_drawer"]').forEach(function (radio) {
      radio.addEventListener('click', function () {
        applyLanguageSelection(radio);
        showView('menu');
      });
      radio.addEventListener('change', function () {
        applyLanguageSelection(radio);
      });
    });

    // Combined form submit — captures button click, Enter on submit button, programmatic submits
    // form.submit() (DOM method) bypasses this listener by spec, so the final line does not recurse
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();

        if (isSubmittingLocale) return;

        if (!hasPendingLocaleChange()) {
          close();
          return;
        }

        isSubmittingLocale = true;
        if (saveBtn) saveBtn.disabled = true;
        setCountryInputsDisabled(true);
        setLanguageInputsDisabled(true);

        // Sync hidden inputs with pending state — final step before navigation.
        // The `disabled = false` is defense-in-depth: submit can only fire from menu
        // view (the save button lives there) and showView('menu') already re-enables
        // menu-view inputs, so in normal flow the hidden inputs are never actually
        // disabled at submit time. Kept as belt-and-suspenders against any future
        // path that could leave them disabled.
        if (hiddenCountryInput) {
          hiddenCountryInput.disabled = false;
          hiddenCountryInput.value = pendingCountryIso;
        }
        if (hiddenLangInput) {
          hiddenLangInput.disabled = false;
          hiddenLangInput.value = pendingLocaleCode;
        }

        // Rewrite return_to from browser URL — server-rendered value loses multi-value
        // filters and doesn't reflect AJAX-applied filters. Then strip stale ?country=.
        var returnToInput = form.querySelector('input[name="return_to"]');
        if (returnToInput) {
          returnToInput.value = window.location.pathname + window.location.search + window.location.hash;
          try {
            var url = new URL(returnToInput.value, window.location.origin);
            url.searchParams.delete('country');
            returnToInput.value = url.pathname + url.search + url.hash;
          } catch (e) {
            // URL parsing failed — keep raw browser URL as fallback
          }
        }

        // Sanitize stale ?country= in current URL — Shopify propagates it via
        // Referer/redirect regardless of return_to, so strip in-place before submit.
        try {
          var currentUrl = new URL(window.location.href);
          if (currentUrl.searchParams.has('country')) {
            currentUrl.searchParams.delete('country');
            history.replaceState(history.state, '', currentUrl.pathname + currentUrl.search + currentUrl.hash);
          }
        } catch (e) {
          // URL parsing failed — leave current URL as-is (fallback)
        }

        try {
          form.submit();
        } catch (e) {
          // Submit threw synchronously (offline, CSP, detached form) — release lock
          // so drawer stays usable. Success path preserves lock (navigation = terminus).
          isSubmittingLocale = false;
          setCountryInputsDisabled(false);
          setLanguageInputsDisabled(false);
          updateSaveButtonState();
        }
      });
    }

    // Document-level Escape listener — works even when focus is outside drawer
    function onDocumentKeyup(e) {
      if (e.code !== 'Escape') return;
      if (!drawer.classList.contains('active')) return;
      close();
    }
    document.addEventListener('keyup', onDocumentKeyup);
    removeDocumentEscapeListener = function () {
      document.removeEventListener('keyup', onDocumentKeyup);
    };

    // Expose open function for trigger buttons
    window.openLocaleDrawer = open;
  }

  init();

  // Re-init on Theme Editor section reload
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-locale-drawer]')) {
      init();
      return;
    }

    // Header reloaded without drawer (both selectors disabled) → prune orphaned body-level drawer
    var isHeaderSection = e.target.classList.contains('section-header') || e.target.querySelector('header-drawer');
    if (isHeaderSection) {
      cleanupBodyDrawer();
    }
  });
})();
